import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { SubscriptionPlan } from '../../types';

interface BkashPaymentModalProps {
  isOpen: boolean;
  onClose: () => void;
  plan: SubscriptionPlan;
  clientData?: {
    email: string;
    password?: string;
    firstName?: string;
    lastName?: string;
    phone?: string;
    companyName?: string;
  };
  resellerId?: string;
  onPaymentComplete: (result: {
    paymentId: string;
    trxId: string;
    subscriptionId: string;
    user?: any;
  }) => void;
}

type PaymentStep = 'initiate' | 'processing' | 'redirect' | 'executing' | 'success' | 'error';

const BkashPaymentModal: React.FC<BkashPaymentModalProps> = ({
  isOpen,
  onClose,
  plan,
  clientData,
  resellerId,
  onPaymentComplete,
}) => {
  const [step, setStep] = useState<PaymentStep>('initiate');
  const [error, setError] = useState<string>('');
  const [paymentData, setPaymentData] = useState<{
    paymentId: string;
    bkashURL: string;
    invoiceNumber: string;
    sandbox: boolean;
  } | null>(null);
  const [result, setResult] = useState<any>(null);

  useEffect(() => {
    if (isOpen) {
      setStep('initiate');
      setError('');
      setPaymentData(null);
      setResult(null);
    }
  }, [isOpen]);

  const handleInitiatePayment = async () => {
    setStep('processing');
    setError('');

    try {
      const response = await api.initiatePayment({
        planId: plan.id,
        clientEmail: clientData?.email,
        clientData,
        resellerId,
      });

      if (response.success && response.data) {
        setPaymentData({
          paymentId: response.data.paymentId,
          bkashURL: response.data.bkashURL,
          invoiceNumber: response.data.invoiceNumber,
          sandbox: response.data.sandbox,
        });
        setStep('redirect');
      } else {
        setError(response.error || 'Failed to initiate payment');
        setStep('error');
      }
    } catch (err: any) {
      setError(err.message || 'Payment initiation failed');
      setStep('error');
    }
  };

  const handleRedirectToBkash = () => {
    if (paymentData?.bkashURL) {
      // For sandbox, simulate payment
      if (paymentData.sandbox) {
        handleSandboxPayment();
      } else {
        // Open bKash payment page in new window
        window.open(paymentData.bkashURL, '_blank', 'width=500,height=600');
        setStep('executing');
        // Poll for payment completion
        pollPaymentStatus();
      }
    }
  };

  const handleSandboxPayment = async () => {
    setStep('executing');

    try {
      // Simulate sandbox payment
      const sandboxResponse = await api.simulateSandboxPayment(paymentData!.paymentId);

      if (sandboxResponse.success) {
        // Execute payment
        const executeResponse = await api.executePayment(paymentData!.paymentId);

        if (executeResponse.success) {
          // Complete subscription
          const completeResponse = await api.completeSubscription({
            paymentId: paymentData!.paymentId,
            clientData,
            resellerId,
          });

          if (completeResponse.success && completeResponse.data) {
            setResult(completeResponse.data);
            setStep('success');
            onPaymentComplete({
              paymentId: paymentData!.paymentId,
              trxId: executeResponse.data!.trxId,
              subscriptionId: completeResponse.data.subscription.id,
              user: completeResponse.data.user,
            });
          } else {
            setError(completeResponse.error || 'Failed to create subscription');
            setStep('error');
          }
        } else {
          setError(executeResponse.error || 'Payment execution failed');
          setStep('error');
        }
      } else {
        setError(sandboxResponse.error || 'Sandbox payment failed');
        setStep('error');
      }
    } catch (err: any) {
      setError(err.message || 'Payment processing failed');
      setStep('error');
    }
  };

  const pollPaymentStatus = async () => {
    const maxAttempts = 60; // 5 minutes
    let attempts = 0;

    const checkStatus = async () => {
      if (attempts >= maxAttempts) {
        setError('Payment timeout. Please try again.');
        setStep('error');
        return;
      }

      try {
        const response = await api.getPaymentStatus(paymentData!.paymentId);

        if (response.success && response.data) {
          if (response.data.status === 'completed') {
            // Complete subscription
            const completeResponse = await api.completeSubscription({
              paymentId: paymentData!.paymentId,
              clientData,
              resellerId,
            });

            if (completeResponse.success && completeResponse.data) {
              setResult(completeResponse.data);
              setStep('success');
              onPaymentComplete({
                paymentId: paymentData!.paymentId,
                trxId: response.data.trxId!,
                subscriptionId: completeResponse.data.subscription.id,
                user: completeResponse.data.user,
              });
            }
          } else if (response.data.status === 'failed' || response.data.status === 'cancelled') {
            setError('Payment was cancelled or failed');
            setStep('error');
          } else {
            // Still pending, check again
            attempts++;
            setTimeout(checkStatus, 5000);
          }
        }
      } catch (err) {
        attempts++;
        setTimeout(checkStatus, 5000);
      }
    };

    setTimeout(checkStatus, 3000);
  };

  const handleManualComplete = async () => {
    setStep('executing');

    try {
      const executeResponse = await api.executePayment(paymentData!.paymentId);

      if (executeResponse.success) {
        const completeResponse = await api.completeSubscription({
          paymentId: paymentData!.paymentId,
          clientData,
          resellerId,
        });

        if (completeResponse.success && completeResponse.data) {
          setResult(completeResponse.data);
          setStep('success');
          onPaymentComplete({
            paymentId: paymentData!.paymentId,
            trxId: executeResponse.data!.trxId,
            subscriptionId: completeResponse.data.subscription.id,
            user: completeResponse.data.user,
          });
        } else {
          setError(completeResponse.error || 'Failed to create subscription');
          setStep('error');
        }
      } else {
        setError(executeResponse.error || 'Payment not completed. Please complete payment in bKash.');
        setStep('redirect');
      }
    } catch (err: any) {
      setError(err.message || 'Payment verification failed');
      setStep('error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-50">
      <div className="bg-[#121212] border border-[#262626] rounded-2xl w-full max-w-md overflow-hidden">
        {/* Header */}
        <div className="p-6 border-b border-[#262626] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-pink-500/20 rounded-xl flex items-center justify-center">
              <span className="text-2xl">💳</span>
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">bKash Payment</h3>
              <p className="text-xs text-neutral-500">Secure payment gateway</p>
            </div>
          </div>
          {step !== 'executing' && (
            <button onClick={onClose} className="text-neutral-400 hover:text-white text-xl">
              ✕
            </button>
          )}
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Plan Summary */}
          <div className="bg-[#1a1a1a] border border-[#262626] rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-neutral-400">Plan</span>
              <span className="text-white font-medium">{plan.name}</span>
            </div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-neutral-400">Duration</span>
              <span className="text-white">{plan.durationDays} days</span>
            </div>
            <div className="flex items-center justify-between pt-2 border-t border-[#262626]">
              <span className="text-neutral-400 font-medium">Total</span>
              <span className="text-emerald-400 text-xl font-bold">৳{plan.priceBdt.toLocaleString()}</span>
            </div>
          </div>

          {/* Step Content */}
          {step === 'initiate' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-pink-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <img
                  src="https://www.logo.wine/a/logo/BKash/BKash-Icon-Logo.wine.svg"
                  alt="bKash"
                  className="w-12 h-12"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
              <p className="text-neutral-400 mb-6">
                Click below to pay with bKash. You will be redirected to complete the payment.
              </p>
              <button
                onClick={handleInitiatePayment}
                className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-medium transition-colors flex items-center justify-center gap-2"
              >
                <span>Pay with bKash</span>
                <span>→</span>
              </button>
            </div>
          )}

          {step === 'processing' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-neutral-400">Initiating payment...</p>
            </div>
          )}

          {step === 'redirect' && paymentData && (
            <div className="text-center">
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-4">
                <p className="text-emerald-400 text-sm">
                  Invoice: <span className="font-mono font-bold">{paymentData.invoiceNumber}</span>
                </p>
              </div>

              {paymentData.sandbox && (
                <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4">
                  <p className="text-yellow-400 text-xs">
                    🧪 Sandbox Mode - Payment will be simulated
                  </p>
                </div>
              )}

              <p className="text-neutral-400 mb-6">
                {paymentData.sandbox
                  ? 'Click below to simulate bKash payment'
                  : 'Click below to open bKash payment page'}
              </p>

              <button
                onClick={handleRedirectToBkash}
                className="w-full py-3 bg-pink-500 hover:bg-pink-600 text-white rounded-xl font-medium transition-colors mb-3"
              >
                {paymentData.sandbox ? 'Simulate Payment' : 'Open bKash'}
              </button>

              {!paymentData.sandbox && (
                <button
                  onClick={handleManualComplete}
                  className="w-full py-2 bg-[#1a1a1a] hover:bg-[#262626] text-neutral-400 rounded-xl text-sm transition-colors"
                >
                  I've completed payment
                </button>
              )}
            </div>
          )}

          {step === 'executing' && (
            <div className="text-center py-8">
              <div className="w-16 h-16 border-4 border-pink-500/30 border-t-pink-500 rounded-full animate-spin mx-auto mb-4" />
              <p className="text-neutral-400 mb-2">Processing payment...</p>
              <p className="text-neutral-500 text-sm">Please wait while we verify your payment</p>
            </div>
          )}

          {step === 'success' && result && (
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">✓</span>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Payment Successful!</h4>
              <p className="text-neutral-400 mb-4">Your subscription has been activated</p>

              <div className="bg-[#1a1a1a] border border-[#262626] rounded-xl p-4 text-left mb-6">
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-500">Transaction ID</span>
                  <span className="text-white font-mono text-sm">{result.payment?.trxId}</span>
                </div>
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-500">Plan</span>
                  <span className="text-white">{result.subscription?.planName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-neutral-500">Valid Until</span>
                  <span className="text-emerald-400">
                    {new Date(result.subscription?.expiresAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <button
                onClick={onClose}
                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl font-medium transition-colors"
              >
                Done
              </button>
            </div>
          )}

          {step === 'error' && (
            <div className="text-center">
              <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-4xl">✕</span>
              </div>
              <h4 className="text-xl font-bold text-white mb-2">Payment Failed</h4>
              <p className="text-red-400 mb-6">{error}</p>

              <div className="flex gap-3">
                <button
                  onClick={() => setStep('initiate')}
                  className="flex-1 py-3 bg-[#1a1a1a] hover:bg-[#262626] text-white rounded-xl font-medium transition-colors"
                >
                  Try Again
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 rounded-xl font-medium transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 pb-6">
          <div className="flex items-center justify-center gap-2 text-xs text-neutral-500">
            <span>🔒</span>
            <span>Secured by bKash Payment Gateway</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BkashPaymentModal;
